import { SafeStorage } from "../storage/storage";
import { CookieOptions } from "../storage/cookie";

export interface UtmPayload {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_page?: string;
  referrer?: string;
  timestamp?: number;
}

export interface AttributionTouches {
  firstTouch: UtmPayload | null;
  lastTouch: UtmPayload | null;
}

export class UtmTracker {
  private static FIRST_TOUCH_KEY = "__thrico_utm_first";
  private static LAST_TOUCH_KEY = "__thrico_utm_last";

  private cookieOptions: CookieOptions;

  constructor(cookieOptions: CookieOptions = {}) {
    this.cookieOptions = cookieOptions;
  }

  /**
   * Capture UTM parameters and referrers from the current browser session
   */
  public capture(): AttributionTouches {
    if (typeof window === "undefined" || typeof location === "undefined") {
      return { firstTouch: null, lastTouch: null };
    }

    const urlParams = new URLSearchParams(window.location.search);
    const hasExplicitUtm =
      urlParams.has("utm_source") ||
      urlParams.has("utm_medium") ||
      urlParams.has("utm_campaign") ||
      urlParams.has("utm_content") ||
      urlParams.has("utm_term");

    const referrer =
      typeof document !== "undefined" ? document.referrer || "" : "";
    const cleanLandingPage = window.location.href.split("?")[0];

    // Build the touch payload from URL or natural referrer
    const currentTouch: UtmPayload = {
      utm_source:
        urlParams.get("utm_source") ||
        (referrer ? this.classifyReferrer(referrer) : "direct"),
      utm_medium:
        urlParams.get("utm_medium") || (referrer ? "referral" : "none"),
      utm_campaign: urlParams.get("utm_campaign") || "organic",
      utm_term: urlParams.get("utm_term") || undefined,
      utm_content: urlParams.get("utm_content") || undefined,
      landing_page: cleanLandingPage,
      referrer,
      timestamp: Date.now(),
    };

    // 1. First Touch: Immutable once captured
    let firstTouch = this.readPayload(UtmTracker.FIRST_TOUCH_KEY);
    if (!firstTouch) {
      firstTouch = currentTouch;
      this.writePayload(UtmTracker.FIRST_TOUCH_KEY, firstTouch, 365); // 1 year retention
    }

    // 2. Last Touch: Updated on explicit UTM arrival or fresh external visit
    let lastTouch = this.readPayload(UtmTracker.LAST_TOUCH_KEY);
    if (hasExplicitUtm || !lastTouch) {
      lastTouch = currentTouch;
      this.writePayload(UtmTracker.LAST_TOUCH_KEY, lastTouch, 30); // 30 days retention
    }

    return { firstTouch, lastTouch };
  }

  /**
   * Retrieve currently stored first and last touch attribution
   */
  public getTouches(): AttributionTouches {
    return {
      firstTouch: this.readPayload(UtmTracker.FIRST_TOUCH_KEY),
      lastTouch: this.readPayload(UtmTracker.LAST_TOUCH_KEY),
    };
  }

  /**
   * Helper to format HTTP headers for client API requests
   */
  public getHeaders(): Record<string, string> {
    const { firstTouch, lastTouch } = this.getTouches();
    return {
      "x-thrico-utm-first": JSON.stringify(firstTouch || {}),
      "x-thrico-utm-last": JSON.stringify(lastTouch || {}),
    };
  }

  /**
   * Classify major organic/social referrers
   */
  private classifyReferrer(referrer: string): string {
    try {
      const host = new URL(referrer).hostname.toLowerCase();
      if (host.includes("google")) return "google";
      if (host.includes("facebook") || host.includes("fb.com"))
        return "facebook";
      if (host.includes("instagram")) return "instagram";
      if (host.includes("linkedin")) return "linkedin";
      if (
        host.includes("twitter") ||
        host.includes("t.co") ||
        host.includes("x.com")
      )
        return "twitter";
      if (host.includes("youtube")) return "youtube";
      if (host.includes("bing")) return "bing";
      return host;
    } catch {
      return "referral";
    }
  }

  private writePayload(key: string, payload: UtmPayload, days: number): void {
    const options: CookieOptions = {
      ...this.cookieOptions,
      maxAgeSeconds: days * 24 * 60 * 60,
    };
    SafeStorage.setItem(key, JSON.stringify(payload), options);
  }

  private readPayload(key: string): UtmPayload | null {
    const raw = SafeStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
}
